import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
	type EntryCalendarSummaryDay as BackendCalendarSummaryDay,
	type EntriesQueryScope,
	useEntryCalendarSummary,
} from "@/shared/cache";
import { entryQueryKeys } from "@/shared/cache/entryQueryKeys";

export type DateRangeInput = { from?: Date; to?: Date };

export type ApiDateRange = {
	startDate: string;
	endDate: string;
};

export type EntrySummary = {
	trackedHours: number;
	exportedHours: number;
	trackedCount: number;
	exportedCount: number;
};

export type CalendarSummaryDay = {
	date: string;
	trackedBillable: number;
	trackedNonBillable: number;
	exportedBillable: number;
	exportedNonBillable: number;
	trackedCount: number;
	exportedCount: number;
};

const EMPTY_SUMMARY: EntrySummary = {
	trackedHours: 0,
	exportedHours: 0,
	trackedCount: 0,
	exportedCount: 0,
};

function pad(n: number): string {
	return String(n).padStart(2, "0");
}

export function formatApiDate(d: Date): string {
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function dateToApiRange(date: Date): ApiDateRange {
	const day = formatApiDate(date);
	return { startDate: day, endDate: day };
}

function parseApiDate(date: string): Date {
	const [year, month, day] = date.split("-").map(Number);
	return new Date(year, month - 1, day);
}

function shiftApiDate(date: string, days: number): string {
	const shifted = parseApiDate(date);
	shifted.setDate(shifted.getDate() + days);
	return formatApiDate(shifted);
}

function isSingleDayRange(range: ApiDateRange): boolean {
	return range.startDate === range.endDate;
}

function isFutureApiDate(date: string): boolean {
	return date > formatApiDate(new Date());
}

export function adjacentDayRanges(range: ApiDateRange): ApiDateRange[] {
	if (!isSingleDayRange(range)) return [];

	const previousDay = shiftApiDate(range.startDate, -1);
	const nextDay = shiftApiDate(range.startDate, 1);
	const ranges = [{ startDate: previousDay, endDate: previousDay }];

	if (!isFutureApiDate(nextDay)) {
		ranges.push({ startDate: nextDay, endDate: nextDay });
	}

	return ranges;
}

export function selectedDateRangeToApiRange(
	date: Date,
	dateRange?: DateRangeInput,
): ApiDateRange {
	if (dateRange?.from || dateRange?.to) {
		const from = dateRange.from ?? dateRange.to;
		const to = dateRange.to ?? dateRange.from;
		if (!from || !to) return dateToApiRange(date);
		return {
			startDate: formatApiDate(from <= to ? from : to),
			endDate: formatApiDate(from <= to ? to : from),
		};
	}
	return dateToApiRange(date);
}

export function monthToVisibleGridRange(
	monthDate: Date,
	weekStartsOn: 0 | 1 = 0,
): ApiDateRange {
	const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
	const start = new Date(first);
	const startOffset = (first.getDay() - weekStartsOn + 7) % 7;
	start.setDate(first.getDate() - startOffset);

	const end = new Date(start);
	end.setDate(start.getDate() + 41);

	return { startDate: formatApiDate(start), endDate: formatApiDate(end) };
}

export function calendarYearRange(monthDate: Date): ApiDateRange {
	const year = monthDate.getFullYear();
	return {
		startDate: `${year}-01-01`,
		endDate: `${year}-12-31`,
	};
}

export function adjacentCalendarYearRanges(monthDate: Date): ApiDateRange[] {
	const month = monthDate.getMonth();
	const ranges: ApiDateRange[] = [];

	if (month < 6) {
		ranges.push(calendarYearRange(new Date(monthDate.getFullYear() - 1, 0, 1)));
	}
	if (month >= 6) {
		ranges.push(calendarYearRange(new Date(monthDate.getFullYear() + 1, 0, 1)));
	}

	return ranges;
}

export function adjacentMonthRanges(
	monthDate: Date,
	weekStartsOn: 0 | 1 = 0,
): ApiDateRange[] {
	return [
		monthToVisibleGridRange(
			new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1),
			weekStartsOn,
		),
		monthToVisibleGridRange(
			new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1),
			weekStartsOn,
		),
	];
}

export function useEntrySummary(range: ApiDateRange) {
	const { startDate, endDate } = range;
	const scope = useMemo<EntriesQueryScope>(
		() => ({
			date: { from: startDate, to: endDate },
			statuses: ["tracked", "reviewed", "exported"],
			includeAttachments: false,
		}),
		[startDate, endDate],
	);
	const { data: days, isLoading } = useEntryCalendarSummary(scope);
	const data = useMemo(
		() =>
			days.reduce<EntrySummary>(
				(summary, day) => {
					summary.trackedHours +=
						(day.byStatus.tracked ?? 0) + (day.byStatus.reviewed ?? 0);
					summary.trackedCount +=
						(day.statusCounts.tracked ?? 0) + (day.statusCounts.reviewed ?? 0);
					summary.exportedHours += day.byStatus.exported ?? 0;
					summary.exportedCount += day.statusCounts.exported ?? 0;
					return summary;
				},
				{ ...EMPTY_SUMMARY },
			),
		[days],
	);
	return { data, isLoading, isSuccess: !isLoading, error: null };
}

function toCalendarSummaryDay(
	day: BackendCalendarSummaryDay,
): CalendarSummaryDay {
	const tracked = day.byStatusBillable.tracked ?? {};
	const reviewed = day.byStatusBillable.reviewed ?? {};
	const exported = day.byStatusBillable.exported ?? {};
	return {
		date: day.date,
		trackedBillable: (tracked.billable ?? 0) + (reviewed.billable ?? 0),
		trackedNonBillable:
			(tracked.nonBillable ?? 0) + (reviewed.nonBillable ?? 0),
		exportedBillable: exported.billable ?? 0,
		exportedNonBillable: exported.nonBillable ?? 0,
		trackedCount:
			(day.statusCounts.tracked ?? 0) + (day.statusCounts.reviewed ?? 0),
		exportedCount: day.statusCounts.exported ?? 0,
	};
}

export function useCalendarSummary(
	range: ApiDateRange,
	_prefetchRanges: ApiDateRange[] = [],
	timekeeperIds?: readonly string[],
) {
	const { startDate, endDate } = range;
	const scope = useMemo<EntriesQueryScope>(
		() => ({
			date: { from: startDate, to: endDate },
			statuses: ["tracked", "reviewed", "exported"],
			timekeeperIds,
			includeAttachments: false,
		}),
		[startDate, endDate, timekeeperIds],
	);
	const { data: days, isLoading } = useEntryCalendarSummary(scope);
	const data = useMemo(() => days.map(toCalendarSummaryDay), [days]);
	return { data, isLoading, isSuccess: !isLoading, error: null };
}

export function useInvalidateEntryData() {
	const queryClient = useQueryClient();
	return useCallback(() => invalidateEntryData(queryClient), [queryClient]);
}

export function invalidateEntryData(queryClient: QueryClient) {
	queryClient.invalidateQueries({ queryKey: entryQueryKeys.root });
}

export function useSelectedApiRange(
	date: Date,
	dateRange?: DateRangeInput,
): ApiDateRange {
	const from = dateRange?.from;
	const to = dateRange?.to;
	return useMemo(
		() => selectedDateRangeToApiRange(date, { from, to }),
		[date, from, to],
	);
}

export function calendarSummaryToDailySums(calendarDays: CalendarSummaryDay[]) {
	const map = new Map();
	for (const day of calendarDays) {
		map.set(day.date, {
			exportedBillable: day.exportedBillable,
			exportedNonBillable: day.exportedNonBillable,
			trackedBillable: day.trackedBillable,
			trackedNonBillable: day.trackedNonBillable,
		});
	}
	return map;
}
